import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import RecentActivity from "../components/RecentActivity";
import ReferrerStats from "../components/ReferrerStats";
import ContractStats from "../components/ContractStats";
import {
  AlertTriangle,
  Users,
  CalendarDays,
  FileClock,
  TrendingUp,
  ChevronRight,
  ClipboardList,
  Activity,
  FileText,
  UserCog,
  Stethoscope,
  Building2,
  Plus,
  Brain,
  CalendarPlus,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const today = new Date().toLocaleDateString("ja-JP", {
    year: "numeric", month: "long", day: "numeric", weekday: "long",
  });

  const stats = [
    { label: "利用者数", value: data?.patients_count ?? "-", icon: Users, color: "#2C5545" },
    { label: "本日の訪問", value: data?.today_visits ?? "-", icon: CalendarDays, color: "#4A6B8C" },
    { label: "今週の訪問", value: data?.week_visits ?? "-", icon: TrendingUp, color: "#3A6B52" },
    { label: "スタッフ", value: data?.staff_count ?? "-", icon: Users, color: "#D4A373" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">DASHBOARD</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1C1C1A]">
          おはようございます、{user?.name}さん
        </h1>
        <p className="text-[#4A4A48] mt-1 font-numeric">{today}</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5 border-[#E5E5E3] bg-white">
            <div className="flex items-start justify-between mb-4">
              <div className="text-xs tracking-[0.1em] uppercase text-[#8A8A86]">{s.label}</div>
              <s.icon size={18} strokeWidth={1.5} style={{ color: s.color }} />
            </div>
            <div className="text-3xl font-bold font-numeric text-[#1C1C1A]">{s.value}</div>
          </Card>
        ))}
      </div>

      {/* Quick navigation cards — all main screens */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-6 bg-[#2C5545] rounded" />
          <h2 className="text-lg font-bold text-[#1C1C1A]">メニュー</h2>
          <div className="text-xs text-[#8A8A86] font-numeric tracking-wider">QUICK ACCESS</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          {[
            { path: "/patients", testid: "nav-card-patients", label: "利用者管理", desc: "利用者一覧・登録", icon: Users, color: "#2C5545", count: data?.patients_count },
            { path: "/schedule", testid: "nav-card-schedule", label: "スケジュール", desc: "訪問予定", icon: CalendarDays, color: "#4A6B8C", count: data?.today_visits },
            { path: "/records", testid: "nav-card-records", label: "看護記録", desc: "訪問看護記録Ⅱ", icon: ClipboardList, color: "#3A6B52" },
            { path: "/evaluations", testid: "nav-card-evaluations", label: "評価ツール", desc: "GAF・PHQ-9", icon: Activity, color: "#8B6424" },
            { path: "/documents", testid: "nav-card-documents", label: "書類管理", desc: "26+書類テンプレ", icon: FileText, color: "#6B5B4D" },
            { path: "/staff", testid: "nav-card-staff", label: "スタッフ", desc: "職員管理", icon: UserCog, color: "#D4A373", count: data?.staff_count },
            { path: "/referrers", testid: "nav-card-referrers", label: "医療機関元", desc: "医療機関・ケアマネ", icon: Stethoscope, color: "#A74141" },
            { path: "/organization", testid: "nav-card-organization", label: "事業所情報", desc: "事業所設定", icon: Building2, color: "#4A4A48" },
          ].map((m) => (
            <button
              key={m.path}
              data-testid={m.testid}
              onClick={() => nav(m.path)}
              className="text-left"
            >
              <Card className="p-4 w-full border-[#E5E5E3] bg-white hover:shadow-md hover:border-[#2C5545]/40 transition-all h-full">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: m.color + "15" }}>
                    <m.icon size={18} style={{ color: m.color }} strokeWidth={1.5} />
                  </div>
                  {m.count != null && (
                    <span className="text-[10px] bg-white border border-[#E5E5E3] px-2 py-0.5 rounded-full font-numeric font-medium text-[#2C5545]">
                      {m.count}
                    </span>
                  )}
                </div>
                <div className="font-bold text-[#1C1C1A] text-sm leading-snug">{m.label}</div>
                <div className="text-[11px] text-[#8A8A86] mt-0.5 leading-tight">{m.desc}</div>
                <div className="flex items-center gap-1 mt-3 text-[11px] text-[#2C5545] opacity-70">
                  <span className="font-numeric tracking-wider">開く</span>
                  <ChevronRight size={11} />
                </div>
              </Card>
            </button>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {(data?.suicide_risk_evals?.length > 0 || data?.expiring_instructions?.length > 0 || data?.instruction_requests_due?.length > 0) && (
        <div className="space-y-3">
          {data?.suicide_risk_evals?.length > 0 && (
            <Card className="p-5 border-[#A74141]/30 bg-[#FDF5F5]">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-[#A74141] mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-[#A74141] mb-1">希死念慮あり — 直ちに対応が必要</div>
                  <div className="text-sm text-[#4A4A48]">
                    直近30日以内にPHQ-9で希死念慮が報告された利用者が <strong>{data.suicide_risk_evals.length}名</strong> います。主治医への連絡・訪問計画の見直しを推奨します。
                  </div>
                </div>
              </div>
            </Card>
          )}
          {data?.expiring_instructions?.length > 0 && (
            <Card className="p-5 border-[#D4A373]/30 bg-[#FEF9F0]">
              <div className="flex items-start gap-3">
                <FileClock size={20} className="text-[#D4A373] mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-[#8B6424] mb-1">指示書期限 7日以内</div>
                  <div className="text-sm text-[#4A4A48]">
                    <strong>{data.expiring_instructions.length}件</strong> の訪問看護指示書が7日以内に期限を迎えます。主治医への更新依頼をご確認ください。
                  </div>
                </div>
              </div>
            </Card>
          )}
          {data?.instruction_requests_due?.length > 0 && (
            <Card className="p-5 border-[#2C5545]/30 bg-[#F0F6F2]" data-testid="instruction-requests-due-card">
              <div className="flex items-start gap-3">
                <FileText size={20} className="text-[#2C5545] mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-[#2C5545] mb-1">訪問看護指示書依頼書 自動作成済み</div>
                  <div className="text-sm text-[#4A4A48] mb-2">
                    期限1ヶ月前になった指示書 <strong>{data.instruction_requests_due.length}件</strong> について、主治医宛の「訪問看護指示書依頼書」の下書きを自動作成しました。内容を確認し主治医へ送付してください。
                  </div>
                  <div className="space-y-1.5">
                    {data.instruction_requests_due.map((r) => (
                      <button
                        key={r.instruction_id}
                        data-testid={`instruction-request-due-${r.instruction_id}`}
                        onClick={() => nav("/documents")}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-[#2C5545]/15 hover:border-[#2C5545]/40 hover:shadow-sm transition text-left"
                      >
                        <span className="text-sm font-medium text-[#1C1C1A] truncate">{r.patient_name || "利用者"}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-[#8B6424] font-numeric">指示書期限 {r.end_date}</span>
                          <span className="inline-flex items-center gap-0.5 text-xs text-[#2C5545] font-medium">依頼書を開く<ChevronRight size={12} /></span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Today's visits + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-[#E5E5E3] bg-white" data-testid="today-visits-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#1C1C1A]">本日の訪問予定</h2>
            <button
              data-testid="view-schedule-button"
              onClick={() => nav("/schedule")}
              className="text-sm text-[#2C5545] hover:underline"
            >
              スケジュール詳細 →
            </button>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 pb-4 border-b border-[#E5E5E3]" data-testid="quick-actions">
            {[
              { testid: "qa-new-record", label: "看護記録", icon: ClipboardList, path: "/records?new=1", color: "#3A6B52" },
              { testid: "qa-new-eval-phq9", label: "PHQ-9", icon: Brain, path: "/evaluations?new=1&type=phq9", color: "#A74141" },
              { testid: "qa-new-eval-gaf", label: "GAF", icon: Activity, path: "/evaluations?new=1&type=gaf", color: "#8B6424" },
              { testid: "qa-new-visit", label: "訪問予定", icon: CalendarPlus, path: "/schedule?new=1", color: "#4A6B8C" },
            ].map((a) => (
              <button
                key={a.testid}
                data-testid={a.testid}
                onClick={() => nav(a.path)}
                className="group flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed hover:border-solid hover:shadow-sm transition"
                style={{ borderColor: a.color + "55" }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition"
                  style={{ background: a.color + "15" }}
                >
                  <Plus size={12} style={{ color: a.color }} strokeWidth={2.5} />
                </div>
                <div className="flex items-center gap-1 min-w-0">
                  <a.icon size={12} style={{ color: a.color }} strokeWidth={1.7} className="shrink-0" />
                  <span className="text-xs font-medium text-[#1C1C1A] truncate">{a.label}</span>
                </div>
              </button>
            ))}
          </div>

          {data?.today_visits_list?.length ? (
            <div className="divide-y divide-[#E5E5E3]">
              {data.today_visits_list.map((v) => (
                <div key={v.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[#1C1C1A]">{v.start_time} - {v.end_time || "未設定"}</div>
                    <div className="text-sm text-[#4A4A48]">{v.service_type || "訪問看護"}</div>
                  </div>
                  <Badge variant="outline" className="border-[#D9E2D5] text-[#2C5545]">
                    {v.status === "completed" ? "完了" : v.status === "cancelled" ? "キャンセル" : "予定"}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-[#8A8A86]">本日の訪問予定はありません</div>
          )}
        </Card>

        <RecentActivity />
      </div>

      {/* 契約状況 集計 */}
      <ContractStats />

      {/* 医療機関別 集計 */}
      <ReferrerStats />
    </div>
  );
}
